import { Preferences } from "@capacitor/preferences";

const GCLID_KEY = "ads_gclid";
const GCLID_EXPIRY_KEY = "ads_gclid_expiry";

const FB_AD_ID_KEY = "ads_fb_ad_id";
const FB_AD_ID_EXPIRY_KEY = "ads_fb_ad_id_expiry";

const FBCLID_KEY = "ads_fbclid";
const FBCLID_EXPIRY_KEY = "ads_fbclid_expiry";

const UTM_KEY = "ads_utm";
const UTM_EXPIRY_KEY = "ads_utm_expiry";

const BCLID_KEY = "ads_bclid";
const BCLID_EXPIRY_KEY = "ads_bclid_expiry";

const BRANCH_ATTRIBUTION_KEY = "ads_branch_attribution";
const BRANCH_ATTRIBUTION_EXPIRY_KEY = "ads_branch_attribution_expiry";

const GCLID_TTL = 90 * 24 * 60 * 60 * 1000; // 90 days
const FBCLID_TTL = 90 * 24 * 60 * 60 * 1000; // 90 days
const FB_AD_ID_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days
const UTM_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days
const BCLID_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days
const BRANCH_ATTRIBUTION_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days


const UTM_PARAMS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
] as const;

type UtmKey = typeof UTM_PARAMS[number];


export interface AdsTrackingData {
    gclid?: string;
    fbclid?: string;
    fb_ad_id?: string;
    utm?: Partial<Record<UtmKey, string>>;
    bclid?: string;
}

export interface AdsTrackingValueWithExpiry<T> {
    value: T;
    expiry: number;
}

export interface AdsTrackingDataWithExpiry {
    gclid?: AdsTrackingValueWithExpiry<string>;
    fbclid?: AdsTrackingValueWithExpiry<string>;
    fb_ad_id?: AdsTrackingValueWithExpiry<string>;
    utm?: AdsTrackingValueWithExpiry<Partial<Record<UtmKey, string>>>;
    bclid?: AdsTrackingValueWithExpiry<string>;
}

let cached: AdsTrackingData | null = null;

let cachedWithExpiry: AdsTrackingDataWithExpiry | null = null;

const saveWithExpiry = async (key: string, expiryKey: string, value: string, ttl: number) => {
    const expiry = Date.now() + ttl;
    await Preferences.set({ key, value });
    await Preferences.set({ key: expiryKey, value: String(expiry) });
};

const loadWithExpiry = async (key: string, expiryKey: string): Promise<string | null> => {
    const { value } = await Preferences.get({ key });
    const { value: expiryStr } = await Preferences.get({ key: expiryKey });

    if (!value || !expiryStr) return null;

    const expiry = Number(expiryStr);
    if (Date.now() > expiry) {
        await Preferences.remove({ key });
        await Preferences.remove({ key: expiryKey });
        return null;
    }

    return value;
};

const loadWithExpiryMeta = async (
    key: string,
    expiryKey: string
): Promise<{ value: string; expiry: number } | null> => {
    const { value } = await Preferences.get({ key });
    const { value: expiryStr } = await Preferences.get({ key: expiryKey });

    if (!value || !expiryStr) return null;

    const expiry = Number(expiryStr);

    if (Date.now() > expiry) {
        await Preferences.remove({ key });
        await Preferences.remove({ key: expiryKey });
        return null;
    }

    return { value, expiry };
};

const setGclid = async (gclid: string) => {
    await saveWithExpiry(GCLID_KEY, GCLID_EXPIRY_KEY, gclid, GCLID_TTL);
};

const getGclid = async (): Promise<string | null> => {
    return await loadWithExpiry(GCLID_KEY, GCLID_EXPIRY_KEY);
};


const setFbAdId = async (fbAdId: string) => {
    await saveWithExpiry(FB_AD_ID_KEY, FB_AD_ID_EXPIRY_KEY, fbAdId, FB_AD_ID_TTL);
};

const getFbAdId = async (): Promise<string | null> => {
    return await loadWithExpiry(FB_AD_ID_KEY, FB_AD_ID_EXPIRY_KEY);
};

const setFbclid = async (fbclid: string) => {
    await saveWithExpiry(FBCLID_KEY, FBCLID_EXPIRY_KEY, fbclid, FBCLID_TTL);
};

const getFbclid = async (): Promise<string | null> => {
    return await loadWithExpiry(FBCLID_KEY, FBCLID_EXPIRY_KEY);
};

const setBclid = async (bclid: string) => {
    await saveWithExpiry(BCLID_KEY, BCLID_EXPIRY_KEY, bclid, BCLID_TTL);
};

const getBclid = async (): Promise<string | null> => {
    return await loadWithExpiry(BCLID_KEY, BCLID_EXPIRY_KEY);
};

const setUtm = async (utm: Partial<Record<UtmKey, string>>) => {
    await saveWithExpiry(UTM_KEY, UTM_EXPIRY_KEY, JSON.stringify(utm), UTM_TTL);
};

const setBranchAttribution = async (branchAttribution: Record<string, string>) => {
    await saveWithExpiry(BRANCH_ATTRIBUTION_KEY, BRANCH_ATTRIBUTION_EXPIRY_KEY, JSON.stringify(branchAttribution), BRANCH_ATTRIBUTION_TTL);
};

export const getBranchAttribution = async (): Promise<Record<string, string> | null> => {
    const val = await loadWithExpiry(BRANCH_ATTRIBUTION_KEY, BRANCH_ATTRIBUTION_EXPIRY_KEY);
    if (!val) return null;
    try {
        return JSON.parse(val);
    } catch {
        return null;
    }
};

const getUtm = async (): Promise<Partial<Record<UtmKey, string>> | null> => {
    const val = await loadWithExpiry(UTM_KEY, UTM_EXPIRY_KEY);
    if (!val) return null;
    try {
        return JSON.parse(val);
    } catch {
        return null;
    }
};

const extractFromUrl = () => {
    if (typeof window === "undefined") return null;

    const params = new URLSearchParams(window.location.search);

    const gclid = params.get("gclid") || undefined;
    const fbclid = params.get("fbclid") || undefined;
    const fb_ad_id = params.get("fb_ad_id") || undefined;

    const utm: Partial<Record<UtmKey, string>> = {};
    UTM_PARAMS.forEach((k) => {
        const v = params.get(k);
        if (v) utm[k] = v;
    });

    return { gclid, fbclid, fb_ad_id, utm };
};

const extractFromBranchInitEvent = (event: any) => {
    const params = event.referringParams;
    const utm: Partial<Record<UtmKey, string>> = {};
    if (params['~channel']) {
        utm.utm_source = params['~channel'];
    }
    if (params['~campaign']) {
        utm.utm_campaign = params['~campaign'];
    }
    if (params['~feature']) {
        utm.utm_medium = params['~feature'];
    }

    return utm;
};

export const setParamsFromBranchInitEvent = async (event: any) => {
    const utm = extractFromBranchInitEvent(event);
    const bclid = event.referringParams['~id'] || undefined;
    let updated = false;
    if (bclid) {
        await setBclid(String(bclid));
        updated = true;
    }
    if (Object.keys(utm).length > 0) {
        await setUtm(utm);
        updated = true;
    }
    if (updated) {
        cached = null;
        cachedWithExpiry = null;
    }
};


export const initAdsTracking = async (): Promise<void> => {
    const data = extractFromUrl();
    if (!data) return;

    if (data.gclid) {
        await setGclid(data.gclid);
    }

    if (data.fbclid) {
        await setFbclid(data.fbclid);
    }

    if (data.fb_ad_id) {
        await setFbAdId(data.fb_ad_id);
    }

    if (data.utm && Object.keys(data.utm).length > 0) {
        await setUtm(data.utm);
    }

    cached = null;
    cachedWithExpiry = null;
};

export const initBranchAttribution = async (params: Record<string, string>): Promise<any> => {
    await setBranchAttribution(params);
}

export const getAdsTrackingData = async (): Promise<AdsTrackingData> => {
    if (cached) return cached;

    const [gclid, fbclid, fb_ad_id, utm, bclid] = await Promise.all([
        getGclid(),
        getFbclid(),
        getFbAdId(),
        getUtm(),
        getBclid(),
    ]);

    cached = {
        ...(gclid ? { gclid } : {}),
        ...(fbclid ? { fbclid } : {}),
        ...(fb_ad_id ? { fb_ad_id } : {}),
        ...(utm ? { utm } : {}),
        ...(bclid ? { bclid } : {}),
    };

    return cached;
};

export const getAdsTrackingDataWithExpiry = async (): Promise<AdsTrackingDataWithExpiry> => {
    if (cachedWithExpiry) return cachedWithExpiry;

    const [gclid, fbclid, fb_ad_id, utm, bclid] = await Promise.all([
        loadWithExpiryMeta(GCLID_KEY, GCLID_EXPIRY_KEY),
        loadWithExpiryMeta(FBCLID_KEY, FBCLID_EXPIRY_KEY),
        loadWithExpiryMeta(FB_AD_ID_KEY, FB_AD_ID_EXPIRY_KEY),
        loadWithExpiryMeta(UTM_KEY, UTM_EXPIRY_KEY),
        loadWithExpiryMeta(BCLID_KEY, BCLID_EXPIRY_KEY),
    ]);

    const result: AdsTrackingDataWithExpiry = {};

    if (gclid) {
        result.gclid = gclid
    }

    if (fbclid) {
        result.fbclid = fbclid
    }

    if (fb_ad_id) {
        result.fb_ad_id = fb_ad_id
    }

    if (bclid) {
        result.bclid = bclid;
    }

    if (utm) {
        try {
            result.utm = {
                value: JSON.parse(utm.value),
                expiry: utm.expiry,
            };
        } catch { }
    }

    cachedWithExpiry = result;
    return result;
};

export const clearAdsTracking = async () => {
    cached = null;
    cachedWithExpiry = null;

    await Preferences.remove({ key: GCLID_KEY });
    await Preferences.remove({ key: GCLID_EXPIRY_KEY });

    await Preferences.remove({ key: FBCLID_KEY });
    await Preferences.remove({ key: FBCLID_EXPIRY_KEY });

    await Preferences.remove({ key: FB_AD_ID_KEY });
    await Preferences.remove({ key: FB_AD_ID_EXPIRY_KEY });

    await Preferences.remove({ key: UTM_KEY });
    await Preferences.remove({ key: UTM_EXPIRY_KEY });

    await Preferences.remove({ key: BCLID_KEY });
    await Preferences.remove({ key: BCLID_EXPIRY_KEY });

    await Preferences.remove({ key: BRANCH_ATTRIBUTION_KEY });
    await Preferences.remove({ key: BRANCH_ATTRIBUTION_EXPIRY_KEY });
};
